/* RapidPlas product specifications accordion — Golden Panda / SF035
   Two theme behaviours are worked around here.

   1. The theme's accordion opens any item that ships with the `open` attribute
      from its own constructor, and 500ms later smooth-scrolls that item into
      the centre of the viewport. On a product page that is what dragged every
      visitor down the page on load — worst on mobile, where the accordion is
      the visible rendering of the tabs.

   2. When it opens an item it measures the panel once, at construction time.
      product-specifications-tab renders one block per variant and hides all of
      them unless the URL carries ?variant=, so at that moment the panel
      measures 0 and is pinned at max-height: 0. The item then reads as open
      while showing nothing, and the first click lands on the theme's "close"
      branch — the dead first click Roy reported.

   So the section no longer passes open_first. We open the first panel here,
   after the page has settled, and keep its height in step with its contents.

   Timing matters more than it looks. This script is rendered inside the
   product section, and deferred scripts run in document order, so it executes
   BEFORE theme.js. Setting `open` at that point just hands the theme the very
   state its constructor scrolls for, and the page scrolls itself again — which
   is exactly what happened on the first attempt at this fix (Dan, 22 Sep 2026).
   So we wait until the theme has upgraded the element and bound its click
   handlers, and only then open the panel. */
(function () {
  'use strict';

  var LIST = '.accordion-product-tabs';
  var ITEM = '.js-accordion-item';
  var BTN = '.js-accordion-button';
  var PANEL = '.js-accordion-content';

  function isVisible(el) {
    return !!(el && el.offsetParent !== null);
  }

  function contentHeight(panel) {
    var inner = panel.children[0];
    return inner ? inner.scrollHeight : 0;
  }

  /* Keep an open panel's inline max-height in step with what is actually in it.
     Writes only on a real change, so the MutationObserver below cannot loop. */
  function sync(item) {
    if (!item.hasAttribute('open')) return;
    var panel = item.querySelector(PANEL);
    var btn = item.querySelector(BTN);
    if (!panel || !btn) return;
    var h = contentHeight(panel);
    if (!h) return;
    var want = h + 'px';
    if (panel.style.maxHeight !== want) panel.style.maxHeight = want;
    if (btn.getAttribute('aria-expanded') !== 'true') btn.setAttribute('aria-expanded', 'true');
  }

  /* Has the theme's accordion upgraded this element yet? Its init() assigns
     button.onclick for every item, so that is the signal. Before it runs, the
     `open` attribute is a scroll trigger; after it runs, it is inert. */
  function themeReady(list) {
    var btn = list.querySelector(BTN);
    return !!(btn && typeof btn.onclick === 'function');
  }

  function syncAll(list) {
    var items = list.querySelectorAll(ITEM);
    for (var i = 0; i < items.length; i++) sync(items[i]);
  }

  /* Open the first panel once. __rpOpened means we have already done it, so a
     shopper who closes the panel keeps it closed. */
  function openFirst(list) {
    if (!isVisible(list)) return;
    var items = list.querySelectorAll(ITEM);
    if (!items.length) return;
    for (var i = 0; i < items.length; i++) {
      if (items[i].hasAttribute('open')) { sync(items[i]); return; }
    }
    if (list.__rpOpened) return;
    if (!themeReady(list)) return;
    list.__rpOpened = true;
    items[0].setAttribute('open', 'true');
    sync(items[0]);
  }

  function watch(list) {
    var run = function () { openFirst(list); syncAll(list); };
    run();
    [0, 150, 400, 900, 1800, 3000].forEach(function (ms) { setTimeout(run, ms); });
    window.addEventListener('load', run);
    window.addEventListener('resize', run);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(run).catch(function () { /* ignore */ });
    }
    if ('MutationObserver' in window) {
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        window.requestAnimationFrame(function () { pending = false; syncAll(list); });
      }).observe(list, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'hidden']
      });
    }
  }

  function init() {
    var lists = document.querySelectorAll(LIST);
    for (var i = 0; i < lists.length; i++) {
      if (lists[i].__rpAccordion) continue;
      lists[i].__rpAccordion = true;
      watch(lists[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
